import { Capacitor } from "@capacitor/core"
import { LocalNotifications } from "@capacitor/local-notifications"

const NOTIFICATION_ID = 1
interface ShiftNotification {
  startsAt: number
  isCurrent: () => boolean
}

let current: ShiftNotification | null = null
let initialization: Promise<void> | undefined
let permissionRequest: Promise<boolean> | undefined
let writes: Promise<void> = Promise.resolve()

function reportError(error: unknown) {
  console.warn("Failed to update shift notification:", error)
}

// Serialize native writes, but never put a permission prompt in this queue.
function enqueueWrite(operation: () => Promise<void>) {
  writes = writes.then(operation).catch(reportError)
  return writes
}

function initialize() {
  return (initialization ??= (async () => {
    if (Capacitor.getPlatform() === "android") {
      await LocalNotifications.createChannel({
        id: "work_tracking",
        name: "Work Tracking",
        description: "Notifications for active work tracking",
        importance: 5,
        visibility: 1,
        lights: false,
        vibration: false
      })
    }
    await LocalNotifications.registerActionTypes({
      types: [
        {
          id: "MANAGE_WORK_PERIOD",
          actions: [{ id: "manage", title: "🕔 Bekijk dienst", foreground: true }]
        }
      ]
    })
    // One listener for the app lifetime; its target is cleared when tracking stops.
    await LocalNotifications.addListener("localNotificationActionPerformed", ({ notification }) => {
      if (notification.id === NOTIFICATION_ID && current?.isCurrent()) {
        void showShiftNotification(current.startsAt, current.isCurrent)
      }
    })
  })().catch((error) => {
    initialization = undefined
    throw error
  }))
}

function ensurePermission() {
  return (permissionRequest ??= (async () => {
    let permission = await LocalNotifications.checkPermissions()
    if (permission.display !== "granted") permission = await LocalNotifications.requestPermissions()
    return permission.display === "granted"
  })().finally(() => {
    permissionRequest = undefined
  }))
}

async function removeNotification() {
  try {
    await LocalNotifications.cancel({ notifications: [{ id: NOTIFICATION_ID }] })
  } finally {
    const delivered = await LocalNotifications.getDeliveredNotifications()
    const notifications = delivered.notifications.filter(
      (notification) => notification.id === NOTIFICATION_ID
    )
    if (notifications.length)
      await LocalNotifications.removeDeliveredNotifications({ notifications })
  }
}

export async function showShiftNotification(startsAt: number, isCurrent: () => boolean) {
  if (!Capacitor.isNativePlatform()) return
  const target = { startsAt, isCurrent }
  current = target
  const isValid = () => current === target && target.isCurrent()
  try {
    await initialize()
    if (!isValid() || !(await ensurePermission())) return
    await enqueueWrite(async () => {
      if (!isValid()) return
      await LocalNotifications.cancel({ notifications: [{ id: NOTIFICATION_ID }] })
      if (!isValid()) return
      const date = new Date(startsAt)
      const time = `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`
      const title = `Sinds ${time} heb je een dienst lopen`
      await LocalNotifications.schedule({
        notifications: [
          {
            id: NOTIFICATION_ID,
            title,
            body: "",
            schedule: { at: new Date(Date.now() + 1000) },
            actionTypeId: "MANAGE_WORK_PERIOD",
            extra: { type: "work_start", workTitle: title },
            ...(Capacitor.getPlatform() === "android"
              ? {
                  ongoing: true,
                  autoCancel: false,
                  iconColor: "#F06C04",
                  smallIcon: "ic_launcher_foreground",
                  channelId: "work_tracking"
                }
              : {})
          }
        ]
      })
      // A stop/replacement can arrive while the native schedule call is pending.
      if (!isValid()) await removeNotification()
    })
  } catch (error) {
    reportError(error)
  }
}

export function clearShiftNotification(): Promise<void> {
  current = null // Invalidate pending permission requests immediately.
  return Capacitor.isNativePlatform() ? enqueueWrite(removeNotification) : Promise.resolve()
}
